import React, { useMemo } from 'react';
import { Alert, Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ChargedAgainstOptionTypeEnum, ITransactionLineToDetail } from 'features/transactions/types';
import { IAuthorizationWithCrosses, ITransactionFinalization } from 'features/transaction-details/types';
import { getAuthorizationRefundImpacts } from 'features/transaction-details/helpers/transactionRefundImpact';
import { RefundImpactNavigationButtons } from '../RefundImpactNavigationButtons';
import {
	computeTransactionAmountSummary,
	formatTransactionAmount,
	ITransactionAmountSummary,
} from 'features/transaction-details/helpers';
import { TransactionFinalizationNotes } from '../TransactionFinalizationNotes';

interface TransactionAmountSummaryProps {
	transaction: ITransactionLineToDetail;
	finalizations?: ITransactionFinalization[];
	authorizations?: IAuthorizationWithCrosses[];
	dealId?: number;
	saleOrderId?: string | null;
	onNavigationError?: (message: string) => void;
}

interface SummaryMetricProps {
	label: string;
	amount: number;
	coinCode: string;
	color?: string;
	helpText?: string;
}

interface SummaryBlockProps {
	title: string;
	blockHelp: React.ReactNode;
	children: React.ReactNode;
}

const HelpTooltip = ({ title }: { title: React.ReactNode }): JSX.Element => (
	<Tooltip
		title={title}
		arrow
		placement="top"
		componentsProps={{
			tooltip: {
				sx: { maxWidth: 360, p: 1.5 },
			},
		}}
	>
		<IconButton size="small" sx={{ p: 0.25 }} aria-label="Ayuda">
			<HelpOutlineIcon fontSize="small" color="action" />
		</IconButton>
	</Tooltip>
);

const SummaryMetric = ({
	label,
	amount,
	coinCode,
	color = 'text.primary',
	helpText,
}: SummaryMetricProps): JSX.Element => (
	<Box sx={{ flex: 1, minWidth: 140 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			{helpText && (
				<HelpTooltip
					title={
						<Typography variant="body2" component="span">
							{helpText}
						</Typography>
					}
				/>
			)}
		</Box>
		<Typography variant="body1" fontWeight="bold" color={color}>
			{formatTransactionAmount(amount, coinCode)}
		</Typography>
	</Box>
);

const SummaryBlock = ({ title, blockHelp, children }: SummaryBlockProps): JSX.Element => (
	<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
			<Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
				{title}
			</Typography>
			<HelpTooltip title={blockHelp} />
		</Box>
		{children}
	</Paper>
);

/**
 * Las autorizaciones por pasarela (dLocal) no se cruzan contra e-tickets: el back las crea con
 * pendingToCross = 0, así que si no se las separa cuentan como "cruzadas".
 */
const computePaymentGatewayCrossInfo = (
	authorizations: IAuthorizationWithCrosses[],
): { gatewayAuthorized: number; gatewayNames: string[]; onlyGateway: boolean } => {
	const gatewayAuthorizations = authorizations.filter(
		(authorization) => authorization.chargedAgainstOptionType === ChargedAgainstOptionTypeEnum.PaymentGateway,
	);
	const gatewayAuthorized = gatewayAuthorizations.reduce(
		(sum, authorization) => sum + Math.max(0, authorization.amountAuthorized),
		0,
	);
	const gatewayNames = Array.from(
		new Set(gatewayAuthorizations.map((authorization) => authorization.chargedAgainstOptionName).filter(Boolean)),
	);

	return {
		gatewayAuthorized,
		gatewayNames,
		onlyGateway: gatewayAuthorizations.length > 0 && gatewayAuthorizations.length === authorizations.length,
	};
};

const buildBlockTooltips = (
	summary: ITransactionAmountSummary,
	crossSummary: ITransactionAmountSummary,
	hasFinalizations: boolean,
): {
	transaction: React.ReactNode;
	authorization: React.ReactNode;
	cross: React.ReactNode;
} => {
	const fmt = (amount: number): string => formatTransactionAmount(amount, summary.coinCode);

	return {
		transaction: (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
				<Typography variant="body2">Monto total cobrado con la tarjeta en esta transacción.</Typography>
				{hasFinalizations && (
					<Typography variant="body2">
						El monto actual refleja la última finalización. El historial de ajustes se muestra junto al total.
					</Typography>
				)}
			</Box>
		),
		authorization: (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Typography variant="body2">
					<strong>Autorizado:</strong> asignado contra un validating carrier (aerolínea u opción de cobro).
				</Typography>
				<Typography variant="body2">
					<strong>Pendiente a autorizar:</strong> aún no tiene autorización contra aerolínea.
				</Typography>
				<Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
					{fmt(summary.totalAmount)} = {fmt(summary.totalAuthorized)} + {fmt(summary.pendingAuthorization)}
				</Typography>
			</Box>
		),
		cross: (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Typography variant="body2">
					<strong>Cruzado:</strong> ya vinculado a uno o más e-tickets.
				</Typography>
				<Typography variant="body2">
					<strong>Pendiente a cruzar:</strong> autorizado y disponible para cruzar contra boleto.
				</Typography>
				<Typography variant="body2">
					<strong>Sin autorizar (no cruzable):</strong> no puede cruzarse hasta autorizarse contra aerolínea.
				</Typography>
				<Typography variant="body2">
					Los cobros por <strong>pasarela de pago</strong> (dLocal) no se cruzan contra e-tickets y no se incluyen acá.
				</Typography>
				<Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
					{fmt(crossSummary.totalAuthorized)} = {fmt(crossSummary.totalCrossed)} + {fmt(crossSummary.pendingToCross)}
				</Typography>
			</Box>
		),
	};
};

export const TransactionAmountSummary = ({
	transaction,
	finalizations = [],
	authorizations = [],
	dealId,
	saleOrderId,
	onNavigationError,
}: TransactionAmountSummaryProps): JSX.Element => {
	const summary = computeTransactionAmountSummary(
		transaction.amount,
		transaction.pendingAuthorization,
		transaction.pendingToCross,
		transaction.coinCode,
	);
	const refundImpacts = useMemo(() => getAuthorizationRefundImpacts(authorizations), [authorizations]);
	const hasFinalizations = finalizations.length > 0;
	const { gatewayAuthorized, gatewayNames, onlyGateway } = useMemo(
		() => computePaymentGatewayCrossInfo(authorizations),
		[authorizations],
	);
	const crossSummary: ITransactionAmountSummary = {
		...summary,
		totalAuthorized: Math.max(0, summary.totalAuthorized - gatewayAuthorized),
		totalCrossed: Math.max(0, summary.totalCrossed - gatewayAuthorized),
	};
	const tooltips = buildBlockTooltips(summary, crossSummary, hasFinalizations);
	const authorizedPercent = summary.totalAmount > 0 ? (summary.totalAuthorized / summary.totalAmount) * 100 : 0;
	const crossableTotal = crossSummary.totalAuthorized;
	const crossedPercent = crossableTotal > 0 ? Math.min(100, (crossSummary.totalCrossed / crossableTotal) * 100) : 0;
	const gatewayLabel = gatewayNames.length > 0 ? gatewayNames.join(', ') : 'pasarela de pago';
	const crossedBarColor = crossedPercent >= 100 ? 'success.main' : 'warning.main';
	const fmt = (amount: number): string => formatTransactionAmount(amount, summary.coinCode);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
			<SummaryBlock title="Monto total de la transacción" blockHelp={tooltips.transaction}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						alignItems: { md: 'flex-start' },
						gap: { xs: 1, md: 2 },
					}}
				>
					<Typography variant="h4" fontWeight="bold" color="primary" sx={{ flexShrink: 0 }}>
						{formatTransactionAmount(summary.totalAmount, summary.coinCode)}
					</Typography>
					{hasFinalizations && (
						<TransactionFinalizationNotes finalizations={finalizations} coinCode={summary.coinCode} />
					)}
				</Box>
			</SummaryBlock>

			<SummaryBlock title="Autorización" blockHelp={tooltips.authorization}>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
					<SummaryMetric
						label="Monto total autorizado"
						amount={summary.totalAuthorized}
						coinCode={summary.coinCode}
						color="success.main"
						helpText="Suma ya asignada a autorizaciones contra validating carrier."
					/>
					<SummaryMetric
						label="Monto pendiente a autorizar"
						amount={summary.pendingAuthorization}
						coinCode={summary.coinCode}
						color="warning.main"
						helpText="Parte del total que aún debe autorizarse contra aerolínea."
					/>
				</Box>
				<Box sx={{ mt: 1.5 }}>
					<Box
						sx={{
							height: 6,
							borderRadius: 3,
							bgcolor: 'grey.200',
							overflow: 'hidden',
							display: 'flex',
						}}
					>
						<Box
							sx={{
								width: `${authorizedPercent}%`,
								bgcolor: 'success.main',
								transition: 'width 0.3s ease',
							}}
						/>
					</Box>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
						{authorizedPercent.toFixed(0)}% del total autorizado
					</Typography>
				</Box>
				{refundImpacts.length > 0 && summary.pendingAuthorization > 0 && (
					<Alert severity="info" sx={{ mt: 1.5 }}>
						<Typography variant="body2" component="div">
							El pendiente a autorizar incluye montos liberados por devoluciones:
							<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
								{refundImpacts.map((impact) => (
									<li key={impact.authorizationId}>
										Autorización #{impact.authorizationId}: {fmt(impact.totalRefunded)}
										{impact.isFullyRefunded ? ' (reembolsada al 100%)' : ' (reembolso parcial)'}
									</li>
								))}
							</ul>
							El <strong>caso de venta {dealId}</strong> recupera saldo pendiente con el cliente hasta que se registre
							una nueva autorización.
						</Typography>
						{dealId != null && (
							<RefundImpactNavigationButtons dealId={dealId} saleOrderId={saleOrderId} onError={onNavigationError} />
						)}
					</Alert>
				)}
			</SummaryBlock>

			<SummaryBlock title="Cruce de e-tickets" blockHelp={tooltips.cross}>
				{onlyGateway ? (
					<Alert severity="info">
						<Typography variant="body2">
							<strong>No aplica:</strong> esta transacción fue autorizada contra {gatewayLabel} (pasarela de pago). Los
							cobros por pasarela no se cruzan contra e-tickets.
						</Typography>
						{summary.pendingAuthorization > 0 && (
							<Typography variant="body2" sx={{ mt: 0.5 }}>
								Si el pendiente ({fmt(summary.pendingAuthorization)}) se autoriza contra aerolínea, ese monto sí va a
								poder cruzarse.
							</Typography>
						)}
					</Alert>
				) : (
					<>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
							<SummaryMetric
								label="Monto total cruzado"
								amount={crossSummary.totalCrossed}
								coinCode={summary.coinCode}
								color="success.main"
								helpText="Monto ya aplicado a e-tickets (cruces activos netos)."
							/>
							<SummaryMetric
								label="Monto pendiente a cruzar"
								amount={summary.pendingToCross}
								coinCode={summary.coinCode}
								color="info.main"
								helpText="Autorizado y listo para vincular a un e-ticket."
							/>
							<SummaryMetric
								label="Sin autorizar (no cruzable)"
								amount={summary.notAuthorizedForCross}
								coinCode={summary.coinCode}
								color="text.secondary"
								helpText="Aún no tiene autorización; no puede cruzarse hasta ese paso."
							/>
						</Box>
						{crossableTotal > 0 && (
							<Box sx={{ mt: 1.5 }}>
								<Box
									sx={{
										height: 6,
										borderRadius: 3,
										bgcolor: 'grey.200',
										overflow: 'hidden',
										display: 'flex',
									}}
								>
									<Box
										sx={{
											width: `${crossedPercent}%`,
											bgcolor: crossedBarColor,
											transition: 'width 0.3s ease, background-color 0.3s ease',
										}}
									/>
								</Box>
								<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
									{crossedPercent.toFixed(0)}% del monto autorizado cruzado
								</Typography>
							</Box>
						)}
						{gatewayAuthorized > 0 && (
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
								No incluye {fmt(gatewayAuthorized)} autorizados contra {gatewayLabel} (pasarela de pago), que no se
								cruzan contra e-tickets.
							</Typography>
						)}
					</>
				)}
			</SummaryBlock>
		</Box>
	);
};
